/**
 * Signering tab while the contract is a draft (ADR-0100): the signing plan.
 *
 * Plans who signs (count, name, capacity per signing slot) and how
 * (method, Ort och datum, deadline/reminders, signing order). Nothing here
 * is identity data: email, personnummer and BankID are entered when the
 * contract is sent, where the roster editor (ADR-0044) starts from this
 * plan. Automatic until the user edits it; "Återställ" returns to
 * automatic. The plan also drives the draft preview (planned names, the
 * Ort/Datum rows and the statement) in both lanes, and copies with
 * "Kopiera från befintligt avtal".
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus, RotateCcw, Trash2 } from 'lucide-react';
import {
  tenantApi,
  type SignerType,
  type SigningDraft,
  type SigningDraftOptions,
  type SigningDraftSigner,
  type SigningMethod,
} from '../api.js';
import { useInvalidate, useTenantQuery } from '../query.js';

/** The plan for a contract; shared by the tab, the signing panel and the legacy preview. */
export function useSigningDraft(contractId: string | null | undefined, enabled = true) {
  return useTenantQuery(
    'signing.get_draft',
    (cid) => tenantApi.signing.get_draft({ contract_id: cid as string }),
    [contractId ?? null],
    { ignoreActiveGroup: true, revalidateOnFocus: false, enabled: enabled && !!contractId },
  );
}

const CAPACITY_OPTIONS: ReadonlyArray<{ value: SignerType | ''; label: string }> = [
  { value: '', label: 'Som sig själv / ej angiven' },
  { value: 'firmatecknare', label: 'Firmatecknare' },
  { value: 'vd', label: 'VD' },
  { value: 'ombud', label: 'Ombud' },
  { value: 'other', label: 'Annat' },
];

type Row = SigningDraftSigner & { key: string };

let rowSeq = 0;
const withKeys = (list: ReadonlyArray<SigningDraftSigner>): Row[] =>
  list.map((s) => ({ role: s.role, ordinal: s.ordinal, signer_type: s.signer_type, name: s.name, key: `r${rowSeq++}` }));

export function SigningPlanPanel({
  contractId,
  onSaved,
}: {
  contractId: string;
  /** Called after a save, e.g. to refresh the live preview. */
  onSaved?: () => void | Promise<void>;
}) {
  const invalidate = useInvalidate();
  const q = useSigningDraft(contractId);
  const draft = q.data;

  const [rows, setRows] = useState<Row[]>([]);
  const [auto, setAuto] = useState(true);
  const [options, setOptions] = useState<SigningDraftOptions>({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Adopt the server plan on load and after every save, unless edits are pending.
  const signature = draft ? JSON.stringify([draft.signers, draft.options, draft.signers_auto]) : '';
  useEffect(() => {
    if (!draft || dirty) return;
    setRows(withKeys(draft.signers));
    setAuto(draft.signers_auto);
    setOptions(draft.options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  if (q.error) {
    return <p className="text-xs text-red-700">Kunde inte läsa signeringsutkastet: {(q.error as Error).message}</p>;
  }
  if (!draft) return <p className="text-xs text-stone-500">Laddar …</p>;

  if (draft.export_only) {
    return (
      <Shell>
        <p className="text-xs text-stone-600">
          Det här dokumentet signeras inte — det exporteras och skickas ut när det är klart.
        </p>
      </Shell>
    );
  }

  const readOnly = !draft.editable;
  const edit = (fn: () => void) => {
    fn();
    setDirty(true);
  };
  const setOpt = <K extends keyof SigningDraftOptions>(key: K, value: SigningDraftOptions[K] | undefined) =>
    edit(() =>
      setOptions((prev) => {
        const next = { ...prev };
        if (value === undefined) delete next[key];
        else next[key] = value;
        return next;
      }),
    );

  const method: SigningMethod | null = draft.forced_method ?? options.method ?? null;
  const placeDateDefault = method ? method === 'manual' : true;
  const includePlaceDate = options.include_place_date ?? placeDateDefault;
  const daysToSign = options.days_to_sign ?? 30;
  const daysToRemind = options.days_to_remind === undefined ? 7 : options.days_to_remind;

  const save = async (signers: SigningDraftSigner[] | null) => {
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.signing.set_draft({ contract_id: contractId, options, signers });
      setDirty(false);
      await Promise.all([invalidate('signing.get_draft', contractId), invalidate('signing.get_signers', contractId)]);
      await onSaved?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const toPayload = (): SigningDraftSigner[] =>
    rows.map(({ role, ordinal, signer_type, name }) => ({ role, ordinal, signer_type, name: name?.trim() ? name.trim() : null }));

  return (
    <Shell>
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <SectionLabel>Undertecknare</SectionLabel>
          {auto ? (
            <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-stone-500">
              Automatiskt
            </span>
          ) : (
            !readOnly && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void save(null)}
                className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-stone-800 hover:underline"
              >
                <RotateCcw className="h-3 w-3" /> Återställ till automatiskt
              </button>
            )
          )}
        </div>
        <p className="text-[11px] leading-relaxed text-stone-500">
          Planera vem som skriver under för varje part. Namnen syns i förhandsgranskningen. E-post,
          personnummer och BankID anges när avtalet skickas för signering.
        </p>
        {draft.slots.length === 0 && (
          <p className="text-xs text-stone-500">Mallen har inga undertecknande parter.</p>
        )}
        {draft.slots.map((slot) => (
          <SlotCard
            key={`${slot.role}:${slot.ordinal}`}
            slot={slot}
            rows={rows.filter((r) => r.role === slot.role && r.ordinal === slot.ordinal)}
            readOnly={readOnly}
            onChange={(key, patch) =>
              edit(() => {
                setAuto(false);
                setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
              })
            }
            onRemove={(key) =>
              edit(() => {
                setAuto(false);
                setRows((prev) => prev.filter((r) => r.key !== key));
              })
            }
            onAdd={() =>
              edit(() => {
                setAuto(false);
                setRows((prev) => {
                  const lastIdx = prev.map((r) => `${r.role}:${r.ordinal}`).lastIndexOf(`${slot.role}:${slot.ordinal}`);
                  const added = withKeys([{ role: slot.role, ordinal: slot.ordinal, signer_type: null, name: null }])[0]!;
                  if (lastIdx < 0) return [...prev, added];
                  return [...prev.slice(0, lastIdx + 1), added, ...prev.slice(lastIdx + 1)];
                });
              })
            }
          />
        ))}
      </section>

      <section className="flex flex-col gap-2 border-t border-stone-100 pt-3">
        <SectionLabel>Signeringssätt</SectionLabel>
        {draft.forced_method === 'manual' ? (
          <p className="text-[11px] text-stone-600">Den här avtalstypen signeras manuellt — ingen e-signering.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(
              [
                [null, 'Bestäm vid signering'],
                ['scrive', 'E-signering (Scrive)'],
                ['manual', 'Manuell signering'],
              ] as const
            ).map(([value, label]) => (
              <label key={label} className="flex cursor-pointer items-center gap-1.5 text-xs text-stone-700">
                <input
                  type="radio"
                  name={`plan-method-${contractId}`}
                  disabled={readOnly}
                  checked={(options.method ?? null) === value}
                  onChange={() => setOpt('method', value ?? undefined)}
                  className="accent-indigo-600"
                />
                {label}
              </label>
            ))}
          </div>
        )}
        {method === 'scrive' && !draft.scrive_provisioned && (
          <p className="text-[11px] text-amber-700">
            E-signering är inte aktiverat för er organisation ännu — avtalet kan ändå signeras manuellt.
          </p>
        )}
        <label className="flex cursor-pointer items-start gap-2 text-xs text-stone-700">
          <input
            type="checkbox"
            disabled={readOnly}
            checked={includePlaceDate}
            onChange={(e) => setOpt('include_place_date', e.target.checked)}
            className="mt-0.5 accent-indigo-600"
          />
          <span>
            Ort och datum vid underskrifterna
            <span className="block text-[11px] text-stone-500">
              {includePlaceDate
                ? 'Utskrift för signering på papper: rader för ort och datum och ”var sitt original”.'
                : method === 'scrive'
                  ? 'Digital signering: datum framgår av vattenstämpeln.'
                  : 'Inga rader för ort och datum.'}
              {options.include_place_date !== undefined && !readOnly && (
                <button
                  type="button"
                  onClick={() => setOpt('include_place_date', undefined)}
                  className="ml-1 underline"
                >
                  Standard
                </button>
              )}
            </span>
          </span>
        </label>
      </section>

      {method !== null && (
        <section className="flex flex-col gap-2 border-t border-stone-100 pt-3">
          <SectionLabel>{method === 'scrive' ? 'Tidsfrist & påminnelser' : 'Bevakning'}</SectionLabel>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-stone-600">
            <label className="flex items-center gap-2">
              <span>{method === 'scrive' ? 'Signeras inom' : 'Påminn om signering inom'}</span>
              <NumberInput
                value={daysToSign}
                disabled={readOnly || (method === 'manual' && options.monitoring_disabled === true)}
                onChange={(n) => setOpt('days_to_sign', n)}
              />
              <span className="text-stone-400">dagar</span>
            </label>
            {method === 'scrive' ? (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={daysToRemind !== null}
                  onChange={(e) => setOpt('days_to_remind', e.target.checked ? 7 : null)}
                  className="accent-indigo-600"
                />
                <span>Auto-påminnelse efter</span>
                <NumberInput
                  value={daysToRemind ?? 7}
                  max={Math.max(1, daysToSign - 1)}
                  disabled={readOnly || daysToRemind === null}
                  onChange={(n) => setOpt('days_to_remind', n)}
                />
                <span className="text-stone-400">dagar</span>
              </label>
            ) : (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={options.monitoring_disabled === true}
                  onChange={(e) => setOpt('monitoring_disabled', e.target.checked || undefined)}
                  className="accent-indigo-600"
                />
                <span>Bevaka inte (inga påminnelser)</span>
              </label>
            )}
          </div>
          {method === 'scrive' && (
            <div className="flex flex-wrap gap-3 text-xs text-stone-700">
              {(
                [
                  ['parallel', 'Alla bjuds in samtidigt'],
                  ['sequential', 'En i taget, i ordningen ovan'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="radio"
                    name={`plan-order-${contractId}`}
                    disabled={readOnly}
                    checked={(options.signing_order ?? 'parallel') === value}
                    onChange={() => setOpt('signing_order', value === 'parallel' ? undefined : value)}
                    className="accent-indigo-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          )}
        </section>
      )}

      {err && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{err}</p>}
      {readOnly ? (
        <p className="text-[11px] text-stone-500">
          {draft.contract_status === 'in_review'
            ? 'Låst under granskning — avbryt eller avsluta granskningen för att ändra.'
            : 'Signeringsutkastet kan inte ändras i avtalets nuvarande status.'}
        </p>
      ) : (
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={busy || !dirty}
            onClick={() => void save(auto ? null : toPayload())}
            className="rounded-md bg-stone-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            {busy ? 'Sparar …' : 'Spara signeringsutkast'}
          </button>
          {dirty && <span className="text-[11px] text-amber-700">Osparade ändringar</span>}
        </div>
      )}
    </Shell>
  );
}

function SlotCard({
  slot,
  rows,
  readOnly,
  onChange,
  onRemove,
  onAdd,
}: {
  slot: SigningDraft['slots'][number];
  rows: Row[];
  readOnly: boolean;
  onChange: (key: string, patch: Partial<SigningDraftSigner>) => void;
  onRemove: (key: string) => void;
  onAdd: () => void;
}) {
  const isCompany = slot.party?.id_type === 'org_no';
  const placeholder = useMemo(
    () => (isCompany ? 'Namn på firmatecknare / ombud' : slot.party ? slot.party.legal_name : 'Namn'),
    [isCompany, slot.party],
  );
  return (
    <div className="rounded-md border border-stone-200 p-2.5">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-stone-800">{slot.label}</span>
        <span className="truncate text-[11px] text-stone-500">
          {slot.party ? slot.party.legal_name : 'Ingen part ännu'}
        </span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {rows.map((r, i) => (
          <li key={r.key} className="flex flex-wrap items-center gap-2">
            <span className="w-4 text-right font-mono text-[10px] text-stone-400">{i + 1}</span>
            <input
              type="text"
              value={r.name ?? ''}
              placeholder={placeholder}
              disabled={readOnly}
              onChange={(e) => onChange(r.key, { name: e.target.value })}
              className="min-w-0 flex-1 rounded-md border border-stone-300 px-2 py-1 text-xs disabled:bg-stone-50"
            />
            <select
              value={r.signer_type ?? ''}
              disabled={readOnly}
              onChange={(e) => onChange(r.key, { signer_type: (e.target.value || null) as SignerType | null })}
              className="rounded-md border border-stone-300 px-1.5 py-1 text-xs disabled:bg-stone-50"
            >
              {CAPACITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {!readOnly && (
              <button
                type="button"
                aria-label="Ta bort undertecknare"
                onClick={() => onRemove(r.key)}
                className="text-stone-400 hover:text-red-700"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </li>
        ))}
        {rows.length === 0 && (
          <li className="text-[11px] text-stone-500">
            Ingen planerad — {isCompany ? 'anges vid signering.' : 'parten skriver under själv.'}
          </li>
        )}
      </ul>
      {!readOnly && (
        <button
          type="button"
          onClick={onAdd}
          className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:underline"
        >
          <Plus className="h-3 w-3" /> Lägg till undertecknare
        </button>
      )}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  disabled,
  max = 90,
}: {
  value: number;
  onChange: (n: number) => void;
  disabled?: boolean;
  max?: number;
}) {
  return (
    <input
      type="number"
      min={1}
      max={max}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const n = Math.round(Number(e.target.value));
        if (Number.isFinite(n) && n >= 1) onChange(Math.min(n, max));
      }}
      className="w-14 rounded-md border border-stone-300 px-2 py-1 text-center font-mono text-xs tabular-nums disabled:opacity-40"
    />
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-stone-200 bg-white p-4">
      <div>
        <h3 className="text-sm font-semibold text-stone-900">Signeringsutkast</h3>
        <p className="text-[11px] text-stone-500">
          Planeras före signeringen. Används som utgångspunkt när avtalet skickas för signering.
        </p>
      </div>
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">{children}</span>
  );
}
